/**
 * Email Auth Hooks
 *
 * React hooks for email/password authentication flows.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../config';
import { ApiError, toApiError } from '../lib/api-error';

/**
 * API response types
 */
interface ApiResponse {
  ok: boolean;
  message?: string;
  error?: string;
}

interface SignupParams {
  email: string;
  password: string;
  name?: string;
  invite?: string;
  /** Validated in-app path the emailed link returns to after verifying. */
  returnTo?: string;
}

interface SigninParams {
  email: string;
  password: string;
  invite?: string;
}

interface SigninResponse extends ApiResponse {
  /** Present only when sign-in stopped short of a session to require an MFA step. */
  mfa?: 'enroll' | 'verify';
}

interface VerifyEmailByTokenParams {
  token: string;
  invite?: string;
}

interface VerifyEmailByCodeParams {
  email: string;
  code: string;
  invite?: string;
}

interface ResetPasswordParams {
  token: string;
  password: string;
}

/**
 * API error class
 */
export class EmailAuthError extends Error {
  code: string;

  constructor(code: string, message: string) {
    super(message);
    this.code = code;
    this.name = 'EmailAuthError';
  }
}

/** Message to show for a failed auth request, or `fallback` for anything unexpected. */
export function authErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof EmailAuthError) return err.message;
  if (err instanceof ApiError && err.userError) return err.message;
  return fallback;
}

/** True when the server could not send the verification email (the account exists, unverified). */
export function isVerificationEmailSendFailure(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'VERIFICATION_EMAIL_SEND_FAILED';
}

/**
 * Helper to make API requests
 */
async function apiRequest<T = ApiResponse>(
  endpoint: string,
  body: unknown
): Promise<T> {
  const res = await fetch(API_URL + endpoint, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });

  // Typed catalog errors (userError: true) keep their code and plain message.
  if (!res.ok) {
    const apiError = await toApiError(res);
    if (apiError.userError) throw apiError;
  }

  const data = await res.json();

  if (!data.ok) {
    throw new EmailAuthError(data.error || 'UNKNOWN', data.message || 'An error occurred');
  }

  return data;
}

/**
 * Hook for email signup
 */
export function useEmailSignup() {
  return useMutation({
    mutationFn: (params: SignupParams) =>
      apiRequest('/auth/email/signup', params),
  });
}

/**
 * Hook for email signin
 */
export function useEmailSignin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: SigninParams) =>
      apiRequest<SigninResponse>('/auth/email/signin', params),
    onSuccess: (data) => {
      // An `mfa` response means no session was granted yet — the caller
      // routes to /login/mfa instead. Only invalidate when a session exists.
      if (!data.mfa) {
        queryClient.invalidateQueries({ queryKey: ['me'] });
      }
    },
  });
}

/**
 * Hook for email verification by token (magic link)
 */
export function useVerifyEmailByToken() {
  // No `me` invalidation: the caller does a full page load into the app, and a
  // refetch here would swap the shell to its loading screen and unmount the
  // verify page mid-flow.
  return useMutation({
    mutationFn: (params: VerifyEmailByTokenParams) =>
      apiRequest('/auth/email/verify', params),
  });
}

/**
 * Hook for email verification by code
 */
export function useVerifyEmailByCode() {
  return useMutation({
    mutationFn: (params: VerifyEmailByCodeParams) =>
      apiRequest('/auth/email/verify', params),
  });
}

/**
 * Hook for resending verification email
 */
export function useResendVerification() {
  return useMutation({
    mutationFn: (params: { email: string; invite?: string; returnTo?: string }) =>
      apiRequest('/auth/email/resend-verification', params),
  });
}

/**
 * Hook for requesting password reset
 */
export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) =>
      apiRequest('/auth/email/forgot-password', { email }),
  });
}

/**
 * Hook for resetting password
 */
export function useResetPassword() {
  return useMutation({
    mutationFn: (params: ResetPasswordParams) =>
      apiRequest('/auth/email/reset-password', params),
  });
}
